import { Referral } from "./refer-earn/Referral";
import { HSOverlay, HSStaticMethods, HSTabs, ICollectionItem } from "preline";
import { MyProfile } from "./my-profile/MyProfile";
import { Notification } from "./notification/Notification";
import { ManageStorage } from "./manage-storage/ManageStorage";
import { ApiAccess } from "./api-access/ApiAccess";
import { WebhookSetup } from "./webhook-setup/WebhookSetup";
import { Subscription } from "./manage-subscription/Subscription";
import { Plans } from "./plans/Plans";
import { LogoutAllSessionModal } from "./my-profile/LogoutAllSessionModal";
import { UserDetailCtx } from "../../contexts/UserDetailCtx";
import { cleanUpAllChildren, initAllComponents } from "../../lib/initAllComponents";

interface ModalTab {
  panel: string;
  tabId: string;
  sidebarId: string;
}

export const MODAL_TABS: Record<string, ModalTab> = {
  'my-profile': { panel: '#vertical-tab-myaccount-profile', tabId: 'myaccount-profile', sidebarId: '#my-account?tab=my-profile' },
  'notifications': { panel: '#vertical-tab-myaccount-notifications', tabId: 'myaccount-notifications', sidebarId: '#my-account?tab=notifications' },
  'refer-and-earn': { panel: '#vertical-tab-myaccount-refer-and-earn', tabId: 'myaccount-refer-and-earn', sidebarId: '#my-account?tab=refer-and-earn' },
  'subscription': { panel: '#vertical-tab-myaccount-subscription', tabId: 'myaccount-subscription', sidebarId: '#my-account?tab=subscription' },
  'storage': { panel: '#vertical-tab-myaccount-storage', tabId: 'myaccount-storage', sidebarId: '#my-account?tab=storage' },
  'plans': { panel: '#vertical-tab-myaccount-plans', tabId: 'myaccount-plans', sidebarId: '#my-account?tab=plans' },
  'API-keys': { panel: '#vertical-tab-myaccount-apikeys', tabId: 'myaccount-apikeys', sidebarId: '#my-account?tab=API-keys' },
  'webhooks': { panel: '#vertical-tab-myaccount-webhooks', tabId: 'myaccount-webhooks', sidebarId: '#my-account?tab=webhooks' },
  'manage-team': { panel: '#vertical-tab-myaccount-team', tabId: 'myaccount-team', sidebarId: '#my-account?tab=manage-team' }
};

export function MyAccountModal() {
  let isModalRendered = false;
  let isHandlerProcessing: null | boolean = null;
  let thisModalInstance: null | ReturnType<typeof MyAccountModal> = null;

  const children = {
    referral: Referral(),
    myProfile: MyProfile(),
    notification: Notification(),
    manageStorage: ManageStorage(),
    subscription: Subscription(),
    plans: Plans(),
    apiAccess: ApiAccess(),
    webhookSetup: WebhookSetup(),
    logoutAllSessionModal: LogoutAllSessionModal(close)
  };

  let $modalElem: null | HTMLElement = null;

  function close() {
    if ($modalElem) {
      HSOverlay.close($modalElem);
      setTimeout(() => {
        // this timeout ensures that Profile Modal's backdrops
        // are removed and properly closed before removing them from the DOM
        if(thisModalInstance) cleanUpAllChildren(thisModalInstance)
        removeModalFromDOM()
      }, 500);
    }
  }

  function clearModalURL() {
    const currentUrl = window.location.href;
    const cleanedUrl = currentUrl.replace(/#my-account\?tab=[^&]*(&|$)/, '');
    window.history.pushState({}, '', cleanedUrl);
    handleURLParameters();
  }

  function initDynamically(this: ReturnType<typeof MyAccountModal>) {
    thisModalInstance = this;
    handleURLParameters(); // Initial URL check
  }

  function addModalToDOM() {
    if(thisModalInstance)
      document.body.insertAdjacentHTML("beforeend", thisModalInstance.render());
  }

  function removeModalFromDOM() {
    const $modalHiddenTrigger = document.getElementById("profile-modal-hidden-trigger");
    const $modalBackdrop = document.getElementById("profile-modal-backdrop");
    const $logoutAllSessionModal = document.getElementById("all-session-logout-modal");
    const $logoutAllSessionModalBackdrop = document.getElementById("all-session-logout-modal-backdrop");
    if ($modalElem && $modalHiddenTrigger && thisModalInstance) {
      if (thisModalInstance) cleanUpAllChildren(thisModalInstance)
      $modalElem.remove(); // remove the modal from the DOM
      if ($logoutAllSessionModal) $logoutAllSessionModal.remove(); // remove the Logout All Sessions model
      if ($modalHiddenTrigger) $modalHiddenTrigger.remove(); // remove the hidden modal trigger from DOM
      if ($modalBackdrop) $modalBackdrop.remove(); // remove the profile modal backdrop if present (sometime this might happen if modal not closed properly)
      if ($logoutAllSessionModalBackdrop) $logoutAllSessionModalBackdrop.remove(); // remove the Logout All Sessions modal backdrop if present (sometime this might happen if modal not closed properly)
      isModalRendered = false;
    }
  }

  function handleURLParameters() {
    if (isHandlerProcessing) return;

    isHandlerProcessing = true;

    if (!thisModalInstance) return;

    const url = new URL(window.location.href);
    const hash = url.hash;

    if (hash.startsWith('#my-account')) {
      if (!isModalRendered) {
        // add the modal to DOM and initialize it if it's not rendered yet
        addModalToDOM();
        HSStaticMethods.autoInit();
        isModalRendered = true;
        initAllComponents(thisModalInstance)
      }
      if ($modalElem?.classList.contains('hidden')){
        const secondOverlay = HSOverlay.getInstance($modalElem,true);
        const element = (secondOverlay as any).element;
        element.isClosePrev = false;
        HSOverlay.open($modalElem!); // open the modal if not already open
      }
      const searchParams = new URLSearchParams(hash.split('?')[1]);
      const tabName = searchParams.get('tab');

      if (tabName && tabName in MODAL_TABS) {
        const { tabId } = MODAL_TABS[tabName];
        const tabElement = document.getElementById(tabId);
        HSTabs.open(tabElement!);
      }
    } else {
      // If we're not on a profile modal URL
      if (isModalRendered) 
        close();
    }

    isHandlerProcessing = false;
  }

  function init() {
    if (!isModalRendered) return; // skip init if modal not rendered yet

    $modalElem = document.getElementById("profile-modal") as HTMLElement;
    const $closeButton = $modalElem?.querySelector('#close-profile-modal-btn') as HTMLElement;

    // Add preline event to reset URL on modal close
    const modalHSCloseBtn = HSOverlay.getInstance($closeButton, true) as ICollectionItem<HSOverlay>;
    modalHSCloseBtn.element.on('close', () => {
      clearModalURL();
      const subscriptionBanner = document.getElementById("subscription-banner");
      if(!subscriptionBanner?.classList.contains("hidden")) {
        subscriptionBanner?.classList.add("hidden");
      }
    });

    const el = HSTabs.getInstance('#profile-tabs-list') as HSTabs;
    el.on('change', ({ prev, current }: { prev: string, current: string; }) => {
      // if the tab is not changed, return
      if(current === prev) return;

      if (current === "#vertical-tab-myaccount-refer-and-earn") {
        children.referral.children.allReferrals.populate();
      } else if (current === "#vertical-tab-myaccount-subscription") {
        children.subscription.populate();
      } else if (current === "#vertical-tab-myaccount-plans") {
        children.plans.populate("yearly");
      } else if (current === "#vertical-tab-myaccount-storage") {
        const userDetails = UserDetailCtx.userDetail;
        if (userDetails && userDetails.id) {
          children.manageStorage.populate(userDetails.id);
        }
      }
    });
  }

  function render() {
    return /*html*/`
    <button id="profile-modal-hidden-trigger" aria-haspopup="dialog" aria-expanded="false" aria-controls="profile-modal"
      data-hs-overlay="#profile-modal" type="button"
      class="hidden">
      Profile Modal Hidden Trigger
    </button>
    <div 
    id="profile-modal" 
    class="hs-overlay hs-overlay-backdrop-open:bg-gray-900/50 hidden size-full fixed top-0 start-0 z-[90] overflow-x-hidden overflow-y-hidden pointer-events-none"
    role="dialog" 
    tabindex="-1" 
    aria-labelledby="hs-full-screen-label"
  
    >
    <div class="hs-overlay-open:mt-0 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-10 opacity-0 transition-all max-w-full max-h-full h-full">
      <div class="flex flex-col bg-white pointer-events-auto max-w-full max-h-full h-full dark:bg-neutral-800">
        <div class="py-3 px-4 border-b dark:border-neutral-700">
          <div class="max-w-4xl mx-auto flex justify-between items-center">
          <h3 id="hs-full-screen-label" class="font-bold text-gray-800 dark:text-white">
            My Account
          </h3>
          <div class="text-end flex gap-4 items-center">
          <button id="close-profile-modal-btn" type="button" class="size-8 inline-flex justify-center items-center gap-x-2 rounded-full border border-transparent bg-gray-100 text-gray-800 hover:bg-gray-200 focus:outline-none focus:bg-gray-200 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-700 dark:hover:bg-neutral-600 dark:text-neutral-400 dark:focus:bg-neutral-600" aria-label="Close" data-hs-overlay="#profile-modal">
            <span class="sr-only">Close</span>
            <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
          </div>
          </div>
        </div>
        <div id="profile-modal-sidebar" class="overflow-y-auto md:min-h-[calc(100vh-57px)] bg-[#F9FAFB] dark:bg-neutral-900 pb-8 md:pb-4 p-4">
          <div class="max-w-4xl mx-auto">
          <div class="flex flex-col lg:flex-row min-h-[calc(100vh-90px)]">
            <div class="w-full overflow-x-auto mb-2 lg:mb-0 lg:max-w-[210px] lg:max-h-[calc(100vh-77px)] lg:overflow-hidden lg:overflow-y-auto lg:sticky lg:top-0 lg:pr-4">
                <nav id="profile-tabs-list" class="flex gap-2 lg:gap-0 lg:flex-col pb-2 lg:pb-0" aria-label="Tabs" role="tablist" aria-orientation="horizontal">
                    <a href="#my-account?tab=my-profile" class="active lg:mt-[2px] last:mt-0 whitespace-nowrap" id="myaccount-profile" aria-selected="true" data-hs-tab="#vertical-tab-myaccount-profile" aria-controls="vertical-tab-myaccount-profile" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                                  <svg class="size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                <span>My Profile</span>
                            </div>
                        </div>
                      </a>
                    <a href="#my-account?tab=notifications" class="whitespace-nowrap lg:mt-[2px] last:mt-0" id="myaccount-notifications" aria-selected="false" data-hs-tab="#vertical-tab-myaccount-notifications" aria-controls="vertical-tab-myaccount-notifications" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                                  <svg class="size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bell"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
                                <span>Notifications</span>
                            </div>
                        </div>
                      </a>
                    <a href="#my-account?tab=refer-and-earn" class="whitespace-nowrap lg:mt-[2px] last:mt-0" id="myaccount-refer-and-earn" aria-selected="false" data-hs-tab="#vertical-tab-myaccount-refer-and-earn" aria-controls="vertical-tab-myaccount-refer-and-earn" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                              <svg class="size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-handshake">
                                <path d="m11 17 2 2a1 1 0 1 0 3-3" />
                                <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
                                <path d="m21 3 1 11h-2" />
                                <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
                                <path d="M3 4h8" />
                              </svg>
                                <span>Refer & Earn</span>
                            </div>
                        </div>
                      </a>
                    <div class="text-slate-500 text-xs font-medium font-['Inter'] uppercase dark:text-neutral-400 mt-[28px!important] pl-3 hidden lg:block">Billing</div>
                    <a href="#my-account?tab=subscription" class="whitespace-nowrap lg:mt-[2px] last:mt-0" id="myaccount-subscription" aria-selected="false" data-hs-tab="#vertical-tab-myaccount-subscription" aria-controls="vertical-tab-myaccount-subscription" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-receipt-text size-4">
                                    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
                                    <path d="M14 8H8" />
                                    <path d="M16 12H8" />
                                    <path d="M13 16H8" />
                                  </svg>
                                <span>Manage Subscription</span>
                            </div>
                        </div>
                      </a>
                      <a href="#my-account?tab=storage" class="whitespace-nowrap lg:mt-[2px] last:mt-0" id="myaccount-storage" aria-selected="false" data-hs-tab="#vertical-tab-myaccount-storage" aria-controls="vertical-tab-myaccount-storage" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-database size-4">
                                  <ellipse cx="12" cy="5" rx="9" ry="3" />
                                  <path d="M3 5V19A9 3 0 0 0 21 19V5" />
                                  <path d="M3 12A9 3 0 0 0 21 12" />
                                </svg>
                                <span>Manage Storage</span>
                            </div>
                        </div>
                      </a>
                      <a href="#my-account?tab=plans" class="whitespace-nowrap lg:mt-[2px] last:mt-0" id="myaccount-plans" aria-selected="false" data-hs-tab="#vertical-tab-myaccount-plans" aria-controls="vertical-tab-myaccount-plans" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                                <svg class="size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-badge-dollar-sign">
                                  <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
                                  <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                                  <path d="M12 18V6" />
                                </svg>
                                <span>Plans</span>
                            </div>
                        </div>
                      </a>
                      <div class="text-slate-500 text-xs font-medium font-['Inter'] uppercase dark:text-neutral-400 lg:mt-[28px!important] pl-3 hidden lg:block">Developer</div>
                      <a href="#my-account?tab=API-keys" class="whitespace-nowrap lg:mt-[2px] last:mt-0" id="myaccount-apikeys" aria-selected="false" data-hs-tab="#vertical-tab-myaccount-apikeys" aria-controls="vertical-tab-myaccount-apikeys" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                              <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="lucide lucide-braces shrink-0 mt-0.5 size-4"
                              >
                                <path
                                  d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1"
                                />
                                <path
                                  d="M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1"
                                />
                              </svg>                                
                                <span>API Access</span>
                            </div>
                        </div>
                      </a>
                      <a href="#my-account?tab=webhooks" class="whitespace-nowrap lg:mt-[2px] last:mt-0" id="myaccount-webhooks" aria-selected="false" data-hs-tab="#vertical-tab-myaccount-webhooks" aria-controls="vertical-tab-myaccount-webhooks" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                              <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="lucide lucide-code-xml shrink-0 mt-0.5 size-4"
                              >
                                <path d="m18 16 4-4-4-4" />
                                <path d="m6 8-4 4 4 4" />
                                <path d="m14.5 4-5 16" />
                              </svg>
                                <span>Webhook Setup</span>
                            </div>
                        </div>
                      </a>
                      <div class="text-slate-500 text-xs font-medium font-['Inter'] uppercase dark:text-neutral-400 mt-[28px!important] pl-3 hidden lg:block">Team</div>
                      <a href="#my-account?tab=manage-team" class="whitespace-nowrap lg:mt-[2px] last:mt-0" id="myaccount-team" aria-selected="false" data-hs-tab="#vertical-tab-myaccount-team" aria-controls="vertical-tab-myaccount-team" role="tab">
                        <div class="flex items-center justify-between rounded-lg py-1.5 px-3 text-slate-600 text-sm font-normal font-['Inter'] border border-transparent hover:bg-white hover:text-slate-900 hs-tab-active:bg-white hs-tab-active:border-gray-200 hs-tab-active:text-slate-900 dark:text-neutral-400 dark:hs-tab-active:bg-neutral-800 dark:hs-tab-active:text-neutral-300 dark:hs-tab-active:border-neutral-700 dark dark:hover:bg-neutral-800 dark:hover:text-neutral-300">
                            <div class="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users size-4">
                                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>                                
                                <span>Manage Team</span>
                            </div>
                        </div>
                      </a>
                  </nav>
            </div>
            <div class="flex items-start gap-4 w-full lg:w-[calc(100%-210px)] lg:pl-2">
                <div class="w-full overflow-x-hidden overflow-y-auto">
                    <!-- Start my profile -->
                    ${children.myProfile.render()}
                    <!-- End my profile -->

                    <!-- Start notification -->
                    ${children.notification.render()}
                    <!-- End notification -->

                    <!-- Start Refer & Earn -->
                    ${children.referral.render()}
                    <!-- End Refer & Earn -->
                  
                    <!-- Start Manage Subscription -->
                    ${children.subscription.render()}
                    <!-- End Manage Subscription -->

                    <!-- Start Manage Storage -->
                    ${children.manageStorage.render()}
                    <!-- End Manage Subscription -->

                    <!-- Start Plans -->
                    ${children.plans.render()}
                    <!-- End Plans -->

                    <!-- Start API Access -->
                    ${children.apiAccess.render()}
                    <!-- End API Access -->

                    <!-- Start Webhook -->
                    ${children.webhookSetup.render()}
                    <!-- End Webhook -->

                    <div id="vertical-tab-myaccount-team" class="hidden" role="tabpanel" aria-labelledby="vertical-tab-myaccount-team">
                      <!-- Account Card -->
                      <!-- <div class="p-4 md:p-5 bg-white border border-gray-200 shadow-sm rounded-xl dark:bg-neutral-800 dark:border-neutral-700 h-[253px] md:h-[146px]"> -->
                        <!-- Start send Invitation section -->
                        <!-- <div>
                          <div>
                            <h3 class="text-lg font-semibold text-gray-800 dark:text-neutral-200">
                              Send Invitation
                            </h3>
                          </div> -->
                          <!-- End Col -->
                          <!-- <div class="mt-2"> -->
                            <!-- Filter Group -->
                            <!-- <div class="grid grid-cols-1 sm:grid-cols-12 gap-2 w-full">
                              <div class="sm:col-span-6">
                                <input id="hs-pro-dappinm" type="text" class="block w-full py-2 px-3 border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60 dark:focus:ring-neutral-600" placeholder="Add email">
                              </div>
                              <div class="sm:col-span-4">
                                <input id="hs-pro-dappinm" type="text" class="block w-full py-2 px-3 border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60 dark:focus:ring-neutral-600" placeholder="Select Workspaces">
                              </div>
                              <div class="sm:col-span-2">
                                <button type="button" class="inline-flex justify-center items-center gap-x-2 py-2 px-3 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-blue-500">
                                  Send
                                </button>
                              </div>
                            </div>
                            <div class="flex md:items-center my-3">
                              <input type="checkbox" class="shrink-0 size-3.5 border-gray-300 mt-1 md:mt-0 rounded text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-600 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800" id="hs-pro-dnshdch" checked />
                              <label for="hs-pro-dnshdch" class="text-[14px] font-normal text-gray-600 ms-1.5 dark:text-neutral-400">
                                Notify recipients via email
                              </label>
                            </div> -->
                            <!-- End Filter Group -->
                          <!-- </div>
                        </div> -->
                        <!-- End send Invitation section -->

                        <!-- Body -->
                        <!-- Table -->
                        <!-- <div class="overflow-x-auto">
                          <div class="min-w-full inline-block align-middle"> -->
                            <!-- Table -->
                            <!-- <table class="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                              <thead>
                                <tr>
                                  <th scope="col" class="min-w-[250px] ">
                                    <div class="pe-4 py-3 text-start flex items-center gap-x-1 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                      Member
                                    </div>
                                  </th>
                        
                                  <th scope="col" class="min-w-48">
                                    <div class="px-4 py-3 text-start flex items-center gap-x-1 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                      Role
                                    </div>
                                  </th>
                        
                                  <th scope="col">
                                    <div class="px-4 py-3 text-start flex items-center gap-x-1 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                      Email
                                    </div>
                                  </th>
                        
                                  <th scope="col" class="min-w-36">
                                    <div class="px-4 py-3 text-start flex items-center gap-x-1 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                      Last activity
                                    </div>
                                  </th>
                        
                                  <th scope="col">
                                    <div class="px-4 py-3 text-start flex items-center gap-x-1 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                      Status
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                        
                              <tbody class="divide-y divide-gray-200 dark:divide-neutral-700">
                                <tr>
                                  <td class="size-px whitespace-nowrap pe-4 py-3">
                                    <div class="w-full flex items-center gap-x-3">
                                      <img class="shrink-0 size-[38px] rounded-full" src="https://images.unsplash.com/photo-1659482633369-9fe69af50bfb?ixlib=rb-4.0.3&amp;ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&amp;auto=format&amp;fit=facearea&amp;facepad=3&amp;w=320&amp;h=320&amp;q=80" alt="Avatar">
                                      <div class="grow">
                                        <span class="text-sm font-medium text-gray-800 dark:text-neutral-200">
                                          James Collins
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      Admin
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      james@site.com
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      Today
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="inline-flex items-center gap-x-1.5 py-1.5 px-2.5 text-xs font-medium bg-teal-100 text-teal-800 rounded-full dark:bg-teal-500/10 dark:text-teal-500">
                                      <span class="size-1.5 inline-block bg-gray-800 rounded-full dark:bg-neutral-200"></span>
                                      Active
                                    </span>
                                  </td>
                                </tr>
                        
                                <tr>
                                  <td class="size-px whitespace-nowrap pe-4 py-3">
                                    <div class="w-full flex items-center gap-x-3">
                                      <span class="flex shrink-0 justify-center items-center size-[38px] bg-white border border-gray-200 text-gray-700 text-xs font-medium uppercase rounded-full dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-300">
                                        L
                                      </span>
                                      <div class="grow">
                                        <span class="text-sm font-medium text-gray-800 dark:text-neutral-200">
                                          James Collins
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      Can view
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      liza@site.com
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      2 days ago
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="inline-flex items-center gap-1.5 py-1.5 px-2 text-xs font-medium bg-gray-100 text-gray-800 rounded-full dark:bg-neutral-500/20 dark:text-neutral-400">
                                      <span class="size-1.5 inline-block bg-gray-800 rounded-full dark:bg-neutral-400"></span>
                                      Inactive
                                    </span>
                                  </td>
                                </tr>
                        
                                <tr>
                                  <td class="size-px whitespace-nowrap pe-4 py-3">
                                    <div class="w-full flex items-center gap-x-3">
                                      <img class="shrink-0 size-[38px] rounded-full" src="https://images.unsplash.com/photo-1601935111741-ae98b2b230b0?ixlib=rb-4.0.3&amp;ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&amp;auto=format&amp;fit=facearea&amp;facepad=2.5&amp;w=320&amp;h=320&amp;q=80" alt="Avatar">
                                      <div class="grow">
                                        <span class="text-sm font-medium text-gray-800 dark:text-neutral-200">
                                          Daniel Hobbs
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      Can edit
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      dhobbs@site.com
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      Today
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="inline-flex items-center gap-1.5 py-1.5 px-2 text-xs font-medium bg-gray-100 text-gray-800 rounded-full dark:bg-neutral-500/20 dark:text-neutral-400">
                                      <span class="size-1.5 inline-block bg-gray-800 rounded-full dark:bg-neutral-400"></span>
                                      Inactive
                                    </span>
                                  </td>
                                </tr>
                        
                                <tr>
                                  <td class="size-px whitespace-nowrap pe-4 py-3">
                                    <div class="w-full flex items-center gap-x-3">
                                      <img class="shrink-0 size-[38px] rounded-full" src="https://images.unsplash.com/photo-1679412330254-90cb240038c5?ixlib=rb-4.0.3&amp;ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&amp;auto=format&amp;fit=facearea&amp;facepad=2.5&amp;w=320&amp;h=320&amp;q=80" alt="Avatar">
                                      <div class="grow">
                                        <span class="text-sm font-medium text-gray-800 dark:text-neutral-200">
                                          Brian Warner
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      Can edit
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      brian@site.com
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      1 months ago
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="inline-flex items-center gap-x-1.5 py-1.5 px-2.5 text-xs font-medium bg-teal-100 text-teal-800 rounded-full dark:bg-teal-500/10 dark:text-teal-500">
                                      <span class="size-1.5 inline-block bg-gray-800 rounded-full dark:bg-neutral-200"></span>
                                      Active
                                    </span>
                                  </td>
                                </tr>
                        
                                <tr>
                                  <td class="size-px whitespace-nowrap pe-4 py-3">
                                    <div class="w-full flex items-center gap-x-3">
                                      <span class="flex shrink-0 justify-center items-center size-[38px] bg-white border border-gray-200 text-gray-700 text-xs font-medium uppercase rounded-full dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-300">
                                        O
                                      </span>
                                      <div class="grow">
                                        <span class="text-sm font-medium text-gray-800 dark:text-neutral-200">
                                          Ols Shols
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      Can view
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      olsshols@site.com
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="text-sm text-gray-600 dark:text-neutral-400">
                                      6 days ago
                                    </span>
                                  </td>
                                  <td class="size-px whitespace-nowrap px-4 py-3">
                                    <span class="inline-flex items-center gap-1.5 py-1.5 px-2 text-xs font-medium bg-gray-100 text-gray-800 rounded-full dark:bg-neutral-500/20 dark:text-neutral-400">
                                      <span class="size-1.5 inline-block bg-gray-800 rounded-full dark:bg-neutral-400"></span>
                                      Inactive
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table> -->
                            <!-- End Table -->
                          <!-- </div>
                        </div> -->
                        <!-- End  Table -->
                        <!-- End Body -->     
                      <!-- </div> -->
                      <div class="p-4 md:p-5 bg-white border border-gray-200 shadow-sm rounded-xl dark:bg-neutral-800 dark:border-neutral-700  md:min-h-[calc(100vh-90px)]">
                        <div>
                          <h3 class="text-lg font-semibold text-gray-800 dark:text-neutral-200 mr-1">
                            Manage Team
                          </h3>
                          <p class="text-sm text-gray-500 dark:text-neutral-500">
                            Manage team members, assign roles, and customize permissions to access workspaces.
                          </p>
                        </div>
                        <!-- <div class="mt-10">
                          <img class="w-full h-auto max-w-full" src="/images/empty_data.png" alt="" title="" width="" height="">
                        </div> -->
                        <div
                          class="p-4 md:p-5 mt-10 flex flex-col font-medium justify-center items-center bg-white dark:bg-neutral-800">
                          <img src="/images/Manage-Team.svg" alt="Manage Team" class="mb-4">
                          <h4 class="block text-4xl text-black mb-4 dark:text-white">Coming Soon!</h4>
                          <p class="block text-sm text-gray-500 dark:text-neutral-500 mx-auto max-w-[500px] text-center">
                            Soon, you'll be able to invite, manage, and collaborate with your team members seamlessly within Feedspace. Stay tuned! Secret Tip: we are working on bringing the Agency Plan. If you read till here, reach out to us to get early beta access of agency plan.
                          </p>                          
                        </div>
                      </div>
                    </div>
                </div>
            </div>
            </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      ${children.logoutAllSessionModal.render()}
      `;
  }

  return { render, children, init, close, initDynamically, handleURLParameters };
}
